<template>
	<CardFooter
		:class="
			cn(
				propertyCardFooterBodyVariants({
					variant,
					size,
					extendedHeight: !!property.poiDistanceTime
				}),
				'min-h-fit',
				props.class
			)
		"
	>
		<div class="flex max-h-fit w-full flex-col">
			<div class="mb-2 flex w-full justify-between">
				<Primitive
					:as="props.asTags.addresses"
					:class="
						cn(
							propertyCardFooterPriceVariants({ size, variant }),
							showActions && 'font-matter-sq md:text-[20px] font-normal'
						)
					"
				>
					{{ formattedAskingPrice }}
				</Primitive>
				<span
					v-if="property.commercialId"
					:class="cn(propertyCardCommercialIdVariants({ variant }), '')"
				>
					{{ property.commercialId }}
				</span>
			</div>

			<div class="flex w-full flex-col">
				<div
					:class="
						cn(
							propertyCardFooterTextsVariants({ size, variant }),
							'mb-2 flex flex-nowrap items-center gap-2 leading-[18px]'
						)
					"
				>
					<PropertyCardItemSeparator
						v-if="!!property.area && shouldDisplayAreaLabel"
						:as="props.asTags.infoItems"
						class="min-w-0 truncate font-Inter text-[14px] font-medium"
					>
						{{ property.area }}m²
						<span class="lowercase">
							{{ getAreaLabel(property.propertyType.identifier) }}
						</span>
					</PropertyCardItemSeparator>
					<PropertyCardItemSeparator
						v-if="!!property.totalArea && shouldDisplayTotalAreaLabel"
						:as="props.asTags.infoItems"
						class="font-Inter text-[14px] font-medium"
					>
						{{ property.totalArea }}m²
						<span class="lowercase">
							{{ getAreaLabel(property.propertyType.identifier) }}
						</span>
					</PropertyCardItemSeparator>

					<PropertyCardItemSeparator
						v-if="!!property.suites"
						:as="props.asTags.infoItems"
						class="font-Inter text-[14px] font-medium"
					>
						{{ property.suites }}
						{{ grammarInflection(property.suites, 'suíte', 'suítes') }}
					</PropertyCardItemSeparator>
					<PropertyCardItemSeparator
						v-if="!property.suites && !!property.bedrooms"
						:as="props.asTags.infoItems"
						class="font-Inter text-[14px] font-medium"
					>
						{{ property.bedrooms }}
						{{ grammarInflection(property.bedrooms, 'quarto', 'quartos') }}
					</PropertyCardItemSeparator>
					<PropertyCardItemSeparator
						v-if="!!property.parkingSpots"
						:as="props.asTags.infoItems"
						class="font-Inter text-[14px] font-medium"
					>
						{{ property.parkingSpots }}
						{{ grammarInflection(property.parkingSpots, 'vaga', 'vagas') }}
					</PropertyCardItemSeparator>
				</div>
				<Primitive
					:as="props.asTags.addresses"
					:class="propertyCardFooterAddressVariants({ size, variant })"
				>
					{{ locationLabel }}
				</Primitive>
			</div>

			<div
				v-if="property.poiDistanceTime"
				:class="propertyCardBadgePoiVariants({ variant })"
			>
				<PropertyCardTag :on-light-surface="variant === 'whiteBackground'">
					<template #icon>
						<IconMaterialSymbolsLightDirectionsWalk class="size-4 shrink-0" />
					</template>
					{{ formattedPoiDistanceTime }}
				</PropertyCardTag>
			</div>

			<PropertyCardActions
				v-if="showActions"
				:property="property"
				:is-property-active="isPropertyActive"
				class="mt-2"
			/>
		</div>
	</CardFooter>
</template>
<script setup lang="ts">
import type { PropertyCardFooterProps } from '.';

import { cn, floatToLocaleString } from '@/utils';
import { DistanceTimeLabels } from '~/core/entities/property';
import {
	getAreaLabel,
	propertyCountableExistsOnPropertyType
} from '~/core/entities/SystemEnums';
import { grammarInflection } from '~~/utils';
import { Primitive } from 'reka-ui';
import { computed } from 'vue';

import {
	propertyCardBadgePoiVariants,
	propertyCardCommercialIdVariants,
	propertyCardFooterAddressVariants,
	propertyCardFooterBodyVariants,
	propertyCardFooterPriceVariants,
	propertyCardFooterTextsVariants,
	PropertyCardItemSeparator
} from '.';
import { CardFooter } from '../ui/card';
import PropertyCardTag from './PropertyCardTag.vue';

const props = withDefaults(defineProps<PropertyCardFooterProps>(), {
	showActions: false,
	asTags: () => ({
		askingPrice: 'p',
		addresses: 'p',
		infoItems: 'span'
	})
});

const formattedAskingPrice = computed(() =>
	floatToLocaleString(props.property.askingPrice || 0, {
		style: 'currency',
		minimumFractionDigits: 0,
		maximumFractionDigits: 0,
		roundingMode: 'ceil'
	})
);

const shouldDisplayAreaLabel = computed(() => {
	return propertyCountableExistsOnPropertyType(
		props.property.propertyType.identifier,
		'area'
	);
});

const shouldDisplayTotalAreaLabel = computed(() => {
	return (
		!props.property.area &&
		propertyCountableExistsOnPropertyType(
			props.property.propertyType.identifier,
			'totalArea'
		)
	);
});

const formattedPoiDistanceTime = computed(() => {
	return DistanceTimeLabels[
		props.property.poiDistanceTime as keyof typeof DistanceTimeLabels
	];
});

const locationLabel = computed(() => {
	const propertyTypeName = props.property.propertyType?.name?.trim();
	const region = props.property.region?.trim();
	const city = props.property.city?.trim();
	const state = props.property.state?.trim();
	const address = [region, city].filter(Boolean).join(', ');
	const addressWithState = [address, state].filter(Boolean).join(' - ');

	return [propertyTypeName, addressWithState].filter(Boolean).join(' em ');
});

const isPropertyActive = computed(() => {
	return props.property.publicationStatus === 'published';
});
</script>
